import React from "react";
import Tooltip from "../../ui/ToolTip";
import { usePageTooltips } from "../../../hooks/usePageTooltips";
import { Calendar, ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { Card, CardContent } from "../../ui/card";

const GeneralForm = React.forwardRef(({ 
  info, 
  handleChange, 
  mode, 
  onModeChange, 
  isDisabled, 
  selectModeRef, 
  translations,
  currentDate,
  setCurrentDate,
  startDate,
  endDate,
  isSelectingEnd,
  handleDateClick,
  clearSelection
}, ref) => {

  const tooltips = usePageTooltips();

  const renderInput = (name, label) => {
    const value = info[name];
    const isEmpty = !value && value !== 0;
    
    return (
      <div className="space-y-2">
        <label htmlFor={name} className="text-sm font-medium text-gray-700 flex items-center">
          {label}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="number"
          id={name}
          name={name}
          value={value}
          onChange={handleChange}
          min="0"
          required
          disabled={isDisabled}
          className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
            ${isEmpty ? 'border-gray-300' : 'border-gray-300'}
            ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        />
      </div>
    );
  };

  const previousMonth = () => {
    if (setCurrentDate) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    }
  };

  const nextMonth = () => {
    if (setCurrentDate) {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    }
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isStartOrEndDate = (date) => {
    return (startDate && date.toDateString() === startDate.toDateString()) ||
           (endDate && date.toDateString() === endDate.toDateString());
  };

  const generateCalendarDays = () => {
    if (!currentDate) return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const monthNamesTranslated = [
    translations("calendar.monthNames.january") || "Janeiro",
    translations("calendar.monthNames.february") || "Fevereiro", 
    translations("calendar.monthNames.march") || "Março",
    translations("calendar.monthNames.april") || "Abril",
    translations("calendar.monthNames.may") || "Maio",
    translations("calendar.monthNames.june") || "Junho",
    translations("calendar.monthNames.july") || "Julho",
    translations("calendar.monthNames.august") || "Agosto",
    translations("calendar.monthNames.september") || "Setembro",
    translations("calendar.monthNames.october") || "Outubro",
    translations("calendar.monthNames.november") || "Novembro",
    translations("calendar.monthNames.december") || "Dezembro",
  ];

  const dayNames = [
    translations("calendar.dayNames.sunday") || "Dom",
    translations("calendar.dayNames.monday") || "Seg",
    translations("calendar.dayNames.tuesday") || "Ter",
    translations("calendar.dayNames.wednesday") || "Qua",
    translations("calendar.dayNames.thursday") || "Qui",
    translations("calendar.dayNames.friday") || "Sex",
    translations("calendar.dayNames.saturday") || "Sáb",
  ];

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">
          {translations("form.general.title")}
        </h1>
        <div className="flex items-center h-8">
          <Tooltip content={tooltips.general}/>
        </div>
      </div>
      
      <div ref={selectModeRef} className="mb-8">
        <label className="text-sm font-medium text-gray-700 block mb-3">
          {translations("form.general.selectMode")}
        </label>
        <div className="flex gap-6 flex-wrap">
          <label className="flex items-center">
            <input
              type="radio"
              name="mode"
              value="weeks"
              checked={mode === 'weeks'}
              onChange={(e) => onModeChange(e.target.value)}
              disabled={isDisabled}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">{translations("form.general.weeks")}</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="mode"
              value="days"
              checked={mode === 'days'}
              onChange={(e) => onModeChange(e.target.value)}
              disabled={isDisabled}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">{translations("form.general.days")}</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="mode"
              value="calendar"
              checked={mode === 'calendar'}
              onChange={(e) => onModeChange(e.target.value)}
              disabled={isDisabled}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="ml-2 text-gray-700">
              {translations("form.general.calendar") || "Calendário"}
            </span>
          </label>
        </div>
      </div>
      
      {(mode === 'weeks' || mode === 'days') && (
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mode === 'weeks' ? 
            renderInput("weeks", translations("form.general.weeks")) :
            renderInput("days", translations("form.general.days"))
          }
        </div>
      )}

      {mode === 'calendar' && (
        <Card className="max-w-4xl mx-auto">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                <Calendar className="w-6 h-6 text-blue-600" />
                {translations("calendar.title") || "Calendário de Horários"}
              </h2>
              <p className="text-gray-600">
                {translations("calendar.subtitle") || "Selecione o período de início e fim dos horários"}
              </p>
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-lg border">
              <h3 className="font-semibold mb-3 text-gray-800">
                {translations("calendar.selectedPeriod") || "Período Selecionado:"}
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {translations("calendar.startDate") || "Início:"}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-md font-medium">
                    {startDate ? formatDate(startDate) : (translations("calendar.notSelected") || 'Não selecionado')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">
                    {translations("calendar.endDate") || "Fim:"}
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-md font-medium">
                    {endDate ? formatDate(endDate) : (translations("calendar.notSelected") || 'Não selecionado')}
                  </span>
                </div>
                {startDate && (
                  <button
                    onClick={clearSelection}
                    disabled={isDisabled}
                    className={`px-3 py-1 bg-red-100 text-red-800 rounded-md hover:bg-red-200 transition-colors flex items-center gap-1 
                      ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <X className="w-4 h-4" />
                    {translations("calendar.clear") || "Limpar"}
                  </button>
                )}
              </div>
              {isSelectingEnd && startDate && (
                <div className="mt-2 p-2 bg-yellow-100 text-yellow-800 rounded text-sm">
                  {translations("calendar.selectEndDate") || "Selecione a data de fim clicando no calendário"}
                </div>
              )}
            </div>

            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={previousMonth}
                  disabled={isDisabled}
                  className={`p-2 rounded-lg transition-colors ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-lg font-semibold">
                  {currentDate ? `${monthNamesTranslated[currentDate.getMonth()]} ${currentDate.getFullYear()}` : ''}
                </h3>
                <button
                  onClick={nextMonth}
                  disabled={isDisabled}
                  className={`p-2 rounded-lg transition-colors ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {generateCalendarDays().map((date, index) => (
                  <div key={index} className="aspect-square">
                    {date ? (
                      <button
                        onClick={() => handleDateClick && handleDateClick(date)}
                        disabled={isDisabled}
                        className={`
                          w-full h-full flex items-center justify-center text-sm rounded-lg transition-all relative
                          ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                          ${isStartOrEndDate(date)
                            ? startDate && date.toDateString() === startDate.toDateString()
                              ? 'bg-green-500 text-white font-bold shadow-md'
                              : 'bg-blue-500 text-white font-bold shadow-md'
                            : isDateInRange(date)
                            ? 'bg-blue-100 text-blue-800'
                            : 'hover:bg-gray-100 text-gray-700'
                          }
                        `}
                      >
                        {date.getDate()}
                        {isStartOrEndDate(date) && (
                          <div className="absolute bottom-2 text-xs font-normal">
                            {startDate && date.toDateString() === startDate.toDateString() ? 
                              (translations("calendar.start") || 'Início') : 
                              (translations("calendar.end") || 'Fim')
                            }
                          </div>
                        )}
                      </button>
                    ) : (
                      <div className="w-full h-full"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-800 mb-2">
                {translations("calendar.instructions.title") || "Instruções:"}
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• {translations("calendar.instructions.step1") || "Clique em uma data para selecionar o início do período"}</li>
                <li>• {translations("calendar.instructions.step2") || "Clique em outra data para selecionar o fim do período"}</li>
                <li>• {translations("calendar.instructions.step3") || "As datas no intervalo ficam destacadas em azul"}</li>
                <li>• {translations("calendar.instructions.step4") || "Use o botão \"Limpar\" para recomeçar a seleção"}</li>
              </ul>
            </div>

            {startDate && endDate && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <h4 className="font-medium text-green-800">
                    {translations("calendar.periodConfigured") || "Período Configurado"}
                  </h4>
                </div>
                <div className="text-sm text-green-700">
                  <p><strong>{translations("calendar.startDate") || "Data de Início"}:</strong> {formatDate(startDate)}</p>
                  <p><strong>{translations("calendar.endDate") || "Data de Fim"}:</strong> {formatDate(endDate)}</p>
                  <p><strong>{translations("calendar.duration") || "Duração"}:</strong> {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1} {translations("calendar.days") || "dias"}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
});

GeneralForm.displayName = 'GeneralForm';

export default GeneralForm;